import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Reports = () => {
  const [format, setFormat] = useState('pdf');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = { format };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(`${API}/export/report`, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="reports-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8">
          <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="reports-title">Reports</h1>
          <p className="text-slate-600 mt-1 font-ibm">Export and download financial reports</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 p-8 rounded-sm"
            data-testid="export-form"
          >
            <h2 className="text-xl font-chivo font-bold text-slate-900 mb-6">Export Financial Report</h2>

            <div className="space-y-6">
              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Report Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="select-format" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        PDF Document
                      </div>
                    </SelectItem>
                    <SelectItem value="csv">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet size={16} />
                        CSV Spreadsheet
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Start Date (Optional)</Label>
                <Input
                  type="date"
                  data-testid="input-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                />
              </div>

              <div>
                <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">End Date (Optional)</Label>
                <Input
                  type="date"
                  data-testid="input-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                />
              </div>

              <Button
                onClick={handleExport}
                disabled={loading}
                data-testid="export-button"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-12 font-ibm font-semibold hover:scale-[0.98] transition-transform"
              >
                {loading ? (
                  'Generating Report...'
                ) : (
                  <>
                    <Download size={18} className="mr-2" />
                    Export Report
                  </>
                )}
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 p-8 rounded-sm"
            data-testid="report-info"
          >
            <h2 className="text-xl font-chivo font-bold text-slate-900 mb-6">Report Information</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-ibm font-semibold text-slate-900 mb-2">PDF Report Includes:</h3>
                <ul className="space-y-2 text-slate-600 font-ibm text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    Financial summary (income, expenses, profit)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    Recent transactions table
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    Professional formatting
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-ibm font-semibold text-slate-900 mb-2">CSV Report Includes:</h3>
                <ul className="space-y-2 text-slate-600 font-ibm text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    All transaction details
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    Import into Excel or Google Sheets
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">✓</span>
                    Raw data for custom analysis
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-slate-600 font-ibm text-sm">
                  <strong>Tip:</strong> Use date filters to generate reports for specific time periods.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Reports;